import { useMemo } from 'react';
import { Card, Typography } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PureIconButton } from '@/ui';
import { IContentBlockInstance } from '@/models/content-page.model';
import { IContentBlockSchemaItem } from '@/models/content-block-schema.model';
import { SupportedLocales } from '@/models/localization.model';
import { getBlockSummary } from '../../helpers/blockSummary';
import { contentBlockTypeLabelKey } from '../../helpers/contentBlockI18n';
import BlockCanvasPreview from '../BlockCanvasPreview/BlockCanvasPreview';
import { CanvasCardBody, CanvasPageFrame } from './SortableBlockCanvasStyles';

type TSortableBlockCardProps = {
  block: IContentBlockInstance;
  selected: boolean;
  summary: string;
  typeLabel: string;
  primaryLocale: SupportedLocales;
  onSelect: () => void;
  onRemove: () => void;
};

const BLOCK_TYPES_WITHOUT_SUMMARY = new Set(['text', 'text_with_heading', 'list_with_heading_text']);

const SortableBlockCard = ({
  block,
  selected,
  summary,
  typeLabel,
  primaryLocale,
  onSelect,
  onRemove,
}: TSortableBlockCardProps) => {
  const { t } = useTranslation(['contentPages']);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        onClick={onSelect}
        style={{
          marginBottom: 8,
          cursor: 'pointer',
          borderColor: selected ? '#1677ff' : undefined,
          boxShadow: selected ? '0 0 0 1px #1677ff' : undefined,
        }}
      >
        <CanvasCardBody>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: 'grab', paddingTop: 4 }}
              aria-label={t('aria_drag_handle')}
            >
              <HolderOutlined />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text strong>{typeLabel}</Typography.Text>
              {summary.trim() && summary !== typeLabel && !BLOCK_TYPES_WITHOUT_SUMMARY.has(block.type) ? (
                <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, marginTop: 4 }}>
                  {summary}
                </Typography.Paragraph>
              ) : null}
            </div>
            <PureIconButton
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={t('aria_remove_block')}
              title={t('aria_remove_block')}
              style={{ color: '#ff4d4f', fontSize: 14, lineHeight: 1, paddingTop: 2 }}
            >
              <DeleteOutlined />
            </PureIconButton>
          </div>
          <CanvasPageFrame>
            <BlockCanvasPreview block={block} primaryLocale={primaryLocale} />
          </CanvasPageFrame>
        </CanvasCardBody>
      </Card>
    </div>
  );
};

type TSortableBlockCanvasProps = {
  blocks: IContentBlockInstance[];
  schemaByType: Record<string, IContentBlockSchemaItem>;
  locales: SupportedLocales[];
  previewLocale?: SupportedLocales;
  selectedId: string | null;
  onReorder: (blocks: IContentBlockInstance[]) => void;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
};

const SortableBlockCanvas = ({
  blocks,
  schemaByType,
  locales,
  previewLocale,
  selectedId,
  onReorder,
  onSelect,
  onRemove,
}: TSortableBlockCanvasProps) => {
  const { t } = useTranslation(['contentPages']);
  const primaryLocale = previewLocale ?? locales[0] ?? SupportedLocales.UK;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => blocks.map((b) => b.id), [blocks]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(blocks, oldIndex, newIndex));
  };

  return (
    <Card title={t('canvas_title')} size="small">
      {blocks.length === 0 ? (
        <Typography.Text type="secondary">{t('canvas_empty')}</Typography.Text>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {blocks.map((block) => {
              const rawLabel = schemaByType[block.type]?.label ?? block.type;
              const label = t(contentBlockTypeLabelKey(block.type), { defaultValue: rawLabel });
              const summary =
                block.type === 'gallery'
                  ? (() => {
                      const data = block.data;
                      if (Array.isArray(data)) {
                        return t('summary_gallery', { count: data.length });
                      }
                      if (data && typeof data === 'object') {
                        const items = (data as Record<string, unknown>).items;
                        if (Array.isArray(items)) {
                          return t('summary_gallery', { count: items.length });
                        }
                      }
                      return getBlockSummary(block, label);
                    })()
                  : getBlockSummary(block, label);
              return (
                <SortableBlockCard
                  key={block.id}
                  block={block}
                  selected={block.id === selectedId}
                  summary={summary}
                  typeLabel={label}
                  primaryLocale={primaryLocale}
                  onSelect={() => onSelect(block.id)}
                  onRemove={() => onRemove(block.id)}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      )}
    </Card>
  );
};

export default SortableBlockCanvas;
